import { css, cx } from "styled-system/css";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, ChevronDown, ChevronUp, Trash2, Calendar, FileText } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale/es";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { generateOdontogramPdf } from "./generateOdontogramPdf";
import { useConfirm } from "@/hooks/useConfirm";
import {
  emptyMedicalQuestionnaire,
  parseMedicalQuestionnaire,
  hasQuestionnaireContent,
  type MedicalQuestionnaire,
  type QuestionnaireItem,
  type YesNo,
} from "./medicalQuestionnaire";
import { resolveCustomFormFields, type CustomQuestion, type FormsConfig } from "./formsConfig";

import { SimpleOdontogramGrid, type SimpleOdontogramHandle } from "./SimpleOdontogramGrid";
import { OdontogramImagesPanel } from "@/components/odontogram/odontogram-images-panel";

// ── Types ─────────────────────────────────────────────────────────────────────

type ClientInfo = {
  id: string;
  firstName: string;
  lastName: string | null;
  email: string;
  phone: string | null;
  address: string | null;
  birthDate: string | null;
};

type ConsultSummary = {
  id: string;
  date: string;
  chiefComplaint: string | null;
  sessionNotes: string | null;
  treatmentsIndicated: string | null;
  treatmentsDone: string | null;
  createdAt: string;
};

type RecordData = {
  id: string;
  clientId: string;
  client: ClientInfo;
  medicalHistory: string | null;
  allergies: string | null;
  medications: string | null;
  generalNotes: string | null;
  medicalQuestionnaire: unknown;
  consults: ConsultSummary[];
};

type ToastState = { type: "success" | "error"; msg: string } | null;


// ── Sub-components ────────────────────────────────────────────────────────────

function FieldTextarea({
  label,
  value,
  onChange,
  placeholder,
  rows = 3,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <div>
      <label
        className={css({
          display: "block",
          fontSize: "xs",
          fontWeight: "medium",
          color: "#374151",
          mb: "1.5",
        })}
      >
        {label}
      </label>
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={css({
          w: "full",
          fontSize: "sm",
          color: "#0f172a",
          bg: "white",
          borderWidth: "1px",
          borderColor: "#e2e8f0",
          rounded: "lg",
          px: "3",
          py: "2",
          resize: "vertical",
          outline: "none",
          lineHeight: "1.5",
          _placeholder: { color: "#94a3b8" },
          _focus: {
            borderColor: "token(colors.brand.500)",
            boxShadow: "0 0 0 2px rgba(99,102,241,0.12)",
          },
        })}
      />
    </div>
  );
}

function YesNoToggle({ value, onChange }: { value: YesNo; onChange: (v: YesNo) => void }) {
  const base = css({
    fontSize: "xs",
    fontWeight: "semibold",
    px: "2.5",
    py: "1",
    rounded: "md",
    borderWidth: "1px",
    cursor: "pointer",
    transition: "transform 160ms cubic-bezier(0.23, 1, 0.32, 1), background 120ms ease",
    _active: { transform: "scale(0.96)" },
  });
  const inactive = css({ bg: "white", borderColor: "#e2e8f0", color: "#64748b", _hover: { bg: "#f8fafc" } });
  const activeYes = css({ bg: "#ecfdf5", borderColor: "#6ee7b7", color: "#047857" });
  const activeNo = css({ bg: "#fef2f2", borderColor: "#fca5a5", color: "#b91c1c" });

  return (
    <div className={css({ display: "flex", gap: "1.5", flexShrink: 0 })}>
      <button
        type="button"
        onClick={() => onChange(value === "yes" ? null : "yes")}
        className={cx(base, value === "yes" ? activeYes : inactive)}
      >
        Sí
      </button>
      <button
        type="button"
        onClick={() => onChange(value === "no" ? null : "no")}
        className={cx(base, value === "no" ? activeNo : inactive)}
      >
        No
      </button>
    </div>
  );
}

function QuestionnaireItemRow({
  item,
  onChange,
}: {
  item: QuestionnaireItem;
  onChange: (next: QuestionnaireItem) => void;
}) {
  if (item.type === "text") {
    return (
      <div
        className={css({
          display: "flex",
          flexDir: "column",
          gap: "1.5",
          py: "2",
          borderBottomWidth: "1px",
          borderColor: "#f1f5f9",
        })}
      >
        <span className={css({ fontSize: "sm", color: "#374151" })}>{item.label}</span>
        <input
          type="text"
          value={item.detail}
          onChange={(e) => onChange({ ...item, detail: e.target.value })}
          placeholder="Respuesta…"
          className={css({
            w: "full",
            fontSize: "xs",
            color: "#0f172a",
            bg: "#f8fafc",
            borderWidth: "1px",
            borderColor: "#e2e8f0",
            rounded: "md",
            px: "2.5",
            py: "1.5",
            outline: "none",
            _placeholder: { color: "#94a3b8" },
            _focus: { borderColor: "token(colors.brand.500)", bg: "white" },
          })}
        />
      </div>
    );
  }

  return (
    <div
      className={css({
        display: "flex",
        flexDir: "column",
        gap: "1.5",
        py: "2",
        borderBottomWidth: "1px",
        borderColor: "#f1f5f9",
      })}
    >
      <div className={css({ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "3" })}>
        <span className={css({ fontSize: "sm", color: "#374151" })}>{item.label}</span>
        <YesNoToggle value={item.answer} onChange={(v) => onChange({ ...item, answer: v })} />
      </div>
      {item.answer === "yes" && (
        <input
          type="text"
          value={item.detail}
          onChange={(e) => onChange({ ...item, detail: e.target.value })}
          placeholder="¿Podría ampliar?"
          className={css({
            w: "full",
            fontSize: "xs",
            color: "#0f172a",
            bg: "#f8fafc",
            borderWidth: "1px",
            borderColor: "#e2e8f0",
            rounded: "md",
            px: "2.5",
            py: "1.5",
            outline: "none",
            _placeholder: { color: "#94a3b8" },
            _focus: { borderColor: "token(colors.brand.500)", bg: "white" },
          })}
        />
      )}
    </div>
  );
}

function CustomFieldRow({
  field,
  value,
  onChange,
}: {
  field: CustomQuestion;
  value: boolean | string | undefined;
  /** `null` = respuesta borrada/sin contestar (el field se saca de customFields). */
  onChange: (next: boolean | string | null) => void;
}) {
  if (field.type === "text") {
    return (
      <FieldTextarea
        label={field.label}
        value={typeof value === "string" ? value : ""}
        onChange={(v) => onChange(v.length > 0 ? v : null)}
        rows={2}
      />
    );
  }

  const yn: YesNo = value === true ? "yes" : value === false ? "no" : null;
  return (
    <div className={css({ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "3" })}>
      <span className={css({ fontSize: "sm", fontWeight: "medium", color: "#374151" })}>{field.label}</span>
      <YesNoToggle value={yn} onChange={(v) => onChange(v === "yes" ? true : v === "no" ? false : null)} />
    </div>
  );
}

function ConsultRow({
  c,
  onDelete,
  onLoadToOdontogram,
  onPrint,
}: {
  c: ConsultSummary;
  onDelete: (id: string) => void;
  onLoadToOdontogram: (id: string) => void;
  onPrint: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const confirm = useConfirm();

  return (
    <div
      className={css({
        borderWidth: "1px",
        borderColor: "#e2e8f0",
        rounded: "lg",
        overflow: "hidden",
        bg: "white",
      })}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={css({
          w: "full",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "3",
          px: "4",
          py: "3",
          bg: "white",
          border: "none",
          cursor: "pointer",
          _hover: { bg: "#f8fafc" },
        })}
      >
        <div className={css({ display: "flex", alignItems: "center", gap: "2", flex: 1, minW: 0 })}>
          <Calendar size={13} className={css({ color: "#94a3b8", flexShrink: 0 })} aria-hidden />
          <span className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a" })}>
            {format(new Date(c.date), "d 'de' MMMM yyyy", { locale: es })}
          </span>
          {c.chiefComplaint && (
            <span className={css({ fontSize: "xs", color: "#64748b", truncate: true })}>
              — {c.chiefComplaint}
            </span>
          )}
        </div>
        {expanded
          ? <ChevronUp size={14} className={css({ color: "#94a3b8", flexShrink: 0 })} aria-hidden />
          : <ChevronDown size={14} className={css({ color: "#94a3b8", flexShrink: 0 })} aria-hidden />
        }
      </button>

      {expanded && (
        <div
          className={css({
            px: "4",
            pb: "4",
            borderTopWidth: "1px",
            borderColor: "#f1f5f9",
            display: "flex",
            flexDir: "column",
            gap: "3",
          })}
        >
          {c.chiefComplaint && (
            <div>
              <p className={css({ fontSize: "xs", fontWeight: "medium", color: "#94a3b8", mb: "1" })}>Motivo de consulta</p>
              <p className={css({ fontSize: "sm", color: "#374151" })}>{c.chiefComplaint}</p>
            </div>
          )}
          {c.sessionNotes && (
            <div>
              <p className={css({ fontSize: "xs", fontWeight: "medium", color: "#94a3b8", mb: "1" })}>Notas de la sesión</p>
              <p className={css({ fontSize: "sm", color: "#374151", whiteSpace: "pre-wrap" })}>{c.sessionNotes}</p>
            </div>
          )}
          {c.treatmentsIndicated && (
            <div>
              <p className={css({ fontSize: "xs", fontWeight: "medium", color: "#94a3b8", mb: "1" })}>Tratamientos indicados</p>
              <p className={css({ fontSize: "sm", color: "#374151", whiteSpace: "pre-wrap" })}>{c.treatmentsIndicated}</p>
            </div>
          )}
          {c.treatmentsDone && (
            <div>
              <p className={css({ fontSize: "xs", fontWeight: "medium", color: "#94a3b8", mb: "1" })}>Tratamientos realizados</p>
              <p className={css({ fontSize: "sm", color: "#374151", whiteSpace: "pre-wrap" })}>{c.treatmentsDone}</p>
            </div>
          )}
          <div className={css({ display: "flex", gap: "2", mt: "1", justifyContent: "flex-end", flexWrap: "wrap" })}>
            <button
              type="button"
              onClick={() => onPrint(c.id)}
              className={css({
                fontSize: "xs",
                fontWeight: "medium",
                color: "#374151",
                bg: "#f1f5f9",
                px: "3",
                py: "1.5",
                rounded: "md",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "1.5",
                _hover: { bg: "#e2e8f0" },
              })}
            >
              Exportar PDF
            </button>
            <button
              type="button"
              onClick={() => onLoadToOdontogram(c.id)}
              className={css({
                fontSize: "xs",
                fontWeight: "medium",
                color: "token(colors.brand.700)",
                bg: "rgba(99,102,241,0.08)",
                px: "3",
                py: "1.5",
                rounded: "md",
                border: "none",
                cursor: "pointer",
                _hover: { bg: "rgba(99,102,241,0.14)" },
              })}
            >
              Cargar en odontograma
            </button>
            <button
              type="button"
              onClick={() => {
                void (async () => {
                  if (
                    await confirm({
                      message: "¿Eliminar esta consulta? Esta acción no se puede deshacer.",
                      tone: "danger",
                      confirmLabel: "Eliminar",
                    })
                  )
                    onDelete(c.id);
                })();
              }}
              className={css({
                fontSize: "xs",
                color: "#ef4444",
                bg: "transparent",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "1",
                px: "2",
                py: "1.5",
                rounded: "md",
                _hover: { bg: "#fef2f2" },
              })}
            >
              <Trash2 size={12} aria-hidden />
              Eliminar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function UnsavedDialog({ onProceed, onCancel }: { onProceed: () => void; onCancel: () => void }) {
  return (
    <div
      className={css({
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bg: "rgba(0,0,0,0.45)",
      })}
      onClick={onCancel}
    >
      <div
        className={css({
          bg: "white",
          rounded: "2xl",
          p: "6",
          maxW: "sm",
          w: "full",
          mx: "4",
          boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
          display: "flex",
          flexDir: "column",
          gap: "4",
        })}
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 className={css({ fontSize: "md", fontWeight: "bold", color: "#0f172a", mb: "1" })}>
            Hay cambios sin guardar
          </h2>
          <p className={css({ fontSize: "sm", color: "#64748b" })}>
            El formulario de nueva consulta tiene texto sin guardar. Si salis ahora, se perderan los datos ingresados.
          </p>
        </div>
        <div className={css({ display: "flex", gap: "2", justifyContent: "flex-end" })}>
          <Button variant="secondary" size="sm" onClick={onCancel}>Quedarme</Button>
          <button
            type="button"
            onClick={onProceed}
            className={css({
              fontSize: "sm",
              fontWeight: "semibold",
              color: "white",
              bg: "#ef4444",
              px: "4",
              py: "2",
              rounded: "lg",
              border: "none",
              cursor: "pointer",
              _hover: { bg: "#dc2626" },
            })}
          >
            Salir sin guardar
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function OdontogramEditor() {
  const { id } = useParams<{ id: string }>();
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const [record, setRecord] = useState<RecordData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [professionalName, setProfessionalName] = useState("");

  const recordRef = useRef<RecordData | null>(null);
  const odontogramRef = useRef<SimpleOdontogramHandle>(null);

  // Session form
  const [chiefComplaint, setChiefComplaint] = useState("");
  const [sessionNotes, setSessionNotes] = useState("");
  const [treatmentsIndicated, setTreatmentsIndicated] = useState("");
  const [treatmentsDone, setTreatmentsDone] = useState("");
  const [customConsultAnswers, setCustomConsultAnswers] = useState<Record<string, boolean | string>>({});

  // Personalización de formularios del consultorio (antecedentes / nueva consulta)
  const [formsConfig, setFormsConfig] = useState<FormsConfig | null>(null);

  // Antecedentes: cuestionario estructurado + datos generales del paciente
  const [questionnaire, setQuestionnaire] = useState<MedicalQuestionnaire>(emptyMedicalQuestionnaire());
  const [clientAddress, setClientAddress] = useState("");
  const [clientBirthDate, setClientBirthDate] = useState("");

  const [saving, setSaving] = useState(false);
  const [savingRecord, setSavingRecord] = useState(false);
  const [toast, setToast] = useState<ToastState>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [historyTab, setHistoryTab] = useState<"consultas" | "imagenes">("consultas");

  const hasUnsavedChanges =
    [chiefComplaint, sessionNotes, treatmentsIndicated, treatmentsDone].some((v) => v.trim().length > 0) ||
    Object.keys(customConsultAnswers).length > 0;

  // ── Toast ──────────────────────────────────────────────────────────────────

  function showToast(type: "success" | "error", msg: string) {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  }

  // ── Prevent accidental close ───────────────────────────────────────────────

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);


  // ── Data loading ───────────────────────────────────────────────────────────

  const loadRecord = useCallback(async () => {
    const [r, rMe] = await Promise.all([
      fetchWithSessionSync(`/api/dashboard/odontogram/records/${id}`, { credentials: "include" }, refresh),
      fetchWithSessionSync("/api/dashboard/me", { credentials: "include" }, refresh),
    ]);
    if (r.status === 404) { setNotFound(true); setLoading(false); return; }
    if (!r.ok) { setLoading(false); return; }

    let config: FormsConfig | null = null;
    if (rMe.ok) {
      const dMe = (await rMe.json()) as { professional?: { name?: string; formsConfig?: FormsConfig | null } };
      if (dMe.professional?.name) setProfessionalName(dMe.professional.name);
      config = dMe.professional?.formsConfig ?? null;
      setFormsConfig(config);
    }

    const data = (await r.json()) as { record: RecordData };
    setRecord(data.record);
    recordRef.current = data.record;
    setQuestionnaire(parseMedicalQuestionnaire(data.record.medicalQuestionnaire, config?.antecedentes ?? undefined));
    setClientAddress(data.record.client.address ?? "");
    setClientBirthDate(data.record.client.birthDate ? data.record.client.birthDate.slice(0, 10) : "");
    setLoading(false);
    if (data.record.consults.length > 0) {
      void loadConsultIntoOdontogram(data.record.id, data.record.consults[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, refresh]);

  useEffect(() => { void loadRecord(); }, [id, refresh]);

  async function loadConsultIntoOdontogram(recordId: string, consultId: string) {
    const r = await fetchWithSessionSync(
      `/api/dashboard/odontogram/records/${recordId}/consults/${consultId}`,
      { credentials: "include" },
      refresh,
    );
    if (!r.ok) return;
    const data = (await r.json()) as { consult: { odontogramJson: unknown } };
    odontogramRef.current?.importData(data.consult.odontogramJson);
  }

  // ── Save ───────────────────────────────────────────────────────────────────

  async function handleSaveConsult() {
    if (!record) return;
    setSaving(true);
    try {
      const r = await fetchWithSessionSync(
        `/api/dashboard/odontogram/records/${record.id}/consults`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            chiefComplaint: chiefComplaint.trim() || null,
            sessionNotes: sessionNotes.trim() || null,
            treatmentsIndicated: treatmentsIndicated.trim() || null,
            treatmentsDone: treatmentsDone.trim() || null,
            odontogramJson: odontogramRef.current?.exportData() ?? { version: "simple-v1", showPrimary: false, teeth: {} },
            customFields: Object.keys(customConsultAnswers).length > 0 ? customConsultAnswers : null,
          }),
          credentials: "include",
        },
        refresh,
      );
      if (!r.ok) {
        const d = (await r.json()) as { error?: string };
        showToast("error", d.error ?? "Error al guardar la consulta.");
        return;
      }
      showToast("success", "Consulta guardada correctamente.");
      setChiefComplaint(""); setSessionNotes(""); setTreatmentsIndicated(""); setTreatmentsDone("");
      setCustomConsultAnswers({});
      await loadRecord();
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveRecord() {
    if (!record) return;
    setSavingRecord(true);
    try {
      const [recordRes, clientRes] = await Promise.all([
        fetchWithSessionSync(
          `/api/dashboard/odontogram/records/${record.id}`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ medicalQuestionnaire: questionnaire }),
            credentials: "include",
          },
          refresh,
        ),
        fetchWithSessionSync(
          `/api/dashboard/clients/${record.clientId}`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              address: clientAddress.trim() || null,
              birthDate: clientBirthDate || null,
            }),
            credentials: "include",
          },
          refresh,
        ),
      ]);
      if (recordRes.ok && clientRes.ok) {
        showToast("success", "Antecedentes guardados.");
        await loadRecord();
      } else {
        showToast("error", "Error al guardar los antecedentes.");
      }
    } finally {
      setSavingRecord(false);
    }
  }

  async function handleDeleteConsult(consultId: string) {
    if (!record) return;
    await fetchWithSessionSync(
      `/api/dashboard/odontogram/records/${record.id}/consults/${consultId}`,
      { method: "DELETE", credentials: "include" },
      refresh,
    );
    await loadRecord();
  }

  async function handleLoadConsult(consultId: string) {
    if (!record) return;
    await loadConsultIntoOdontogram(record.id, consultId);
  }

  // ── PDF — generado 100% desde datos, sin captura de DOM ───────────────────
  //
  // Para la consulta actual: usa collectExportPayload() para el odontogramJson
  // (estado actual del odontograma en memoria) + los campos del formulario.
  // Para consultas históricas: fetchea el odontogramJson guardado en DB.
  // generateOdontogramPdf() construye el PDF directamente desde datos con jsPDF
  // y llama a doc.save() — descarga inmediata, sin ventana nueva, sin timing.

  async function handleExportPdf(consultId?: string) {
    if (!record || isPrinting) return;
    setIsPrinting(true);

    try {
      const clientName = [record.client.firstName, record.client.lastName].filter(Boolean).join(" ");

      let printDate = new Date().toISOString();
      let chiefComplaintVal = chiefComplaint.trim() || null;
      let sessionNotesVal = sessionNotes.trim() || null;
      let treatmentsIndicatedVal = treatmentsIndicated.trim() || null;
      let treatmentsDoneVal = treatmentsDone.trim() || null;
      let odontogramJson: unknown = consultId ? null : (odontogramRef.current?.exportData() ?? { version: "simple-v1", showPrimary: false, teeth: {} });

      if (consultId) {
        const r = await fetchWithSessionSync(
          `/api/dashboard/odontogram/records/${record.id}/consults/${consultId}`,
          { credentials: "include" },
          refresh,
        );
        if (!r.ok) { showToast("error", "No se pudo obtener la consulta."); return; }
        const { consult } = (await r.json()) as {
          consult: {
            date: string;
            chiefComplaint: string | null;
            sessionNotes: string | null;
            treatmentsIndicated: string | null;
            treatmentsDone: string | null;
            odontogramJson: unknown;
          };
        };
        printDate = consult.date;
        chiefComplaintVal = consult.chiefComplaint;
        sessionNotesVal = consult.sessionNotes;
        treatmentsIndicatedVal = consult.treatmentsIndicated;
        treatmentsDoneVal = consult.treatmentsDone;
        odontogramJson = consult.odontogramJson;
      }

      generateOdontogramPdf({
        clientName,
        clientEmail: record.client.email,
        clientPhone: record.client.phone,
        clientAddress: record.client.address,
        clientBirthDate: record.client.birthDate,
        professionalName,
        date: printDate,
        chiefComplaint: chiefComplaintVal,
        sessionNotes: sessionNotesVal,
        treatmentsIndicated: treatmentsIndicatedVal,
        treatmentsDone: treatmentsDoneVal,
        medicalHistory: record.medicalHistory,
        allergies: record.allergies,
        medications: record.medications,
        generalNotes: record.generalNotes,
        medicalQuestionnaire: questionnaire,
        odontogramJson,
      });

      showToast("success", "PDF generado y descargado.");
    } catch (err) {
      console.error("PDF generation error:", err);
      showToast("error", "Error al generar el PDF.");
    } finally {
      setIsPrinting(false);
    }
  }

  // ── Guards ─────────────────────────────────────────────────────────────────

  if (loading) return <div className={css({ p: "8" })}><LoadingFrame variant="light" minHeight={320} /></div>;

  if (notFound || !record) {
    return (
      <div className={css({ p: "8", display: "flex", flexDir: "column", alignItems: "center", gap: "4" })}>
        <p className={css({ fontSize: "md", color: "#64748b" })}>Historia clinica no encontrada.</p>
        <button type="button" onClick={() => navigate("/dashboard/odontogram")}
          className={css({ fontSize: "sm", color: "token(colors.brand.600)", textDecoration: "underline", bg: "transparent", border: "none", cursor: "pointer" })}>
          Volver a la lista
        </button>
      </div>
    );
  }

  const clientName = [record.client.firstName, record.client.lastName].filter(Boolean).join(" ");

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={css({ display: "flex", flexDir: "column", bg: "white", minH: "100vh", overflowX: "hidden", w: "100%" })}>

      {/* Sticky header */}
      <div
        className={css({
          display: "flex",
          alignItems: "center",
          gap: "3",
          px: "5",
          py: "3",
          borderBottomWidth: "1px",
          borderColor: "#e2e8f0",
          bg: "white",
          position: "sticky",
          top: "0",
          zIndex: 10,
        })}
      >
        <button
          type="button"
          onClick={() => hasUnsavedChanges ? setConfirmLeave(true) : navigate("/dashboard/odontogram")}
          className={css({ display: "flex", alignItems: "center", gap: "1", fontSize: "sm", color: "#64748b", bg: "transparent", border: "none", cursor: "pointer", flexShrink: 0, _hover: { color: "#0f172a" } })}
        >
          <ArrowLeft size={14} aria-hidden />
          Volver
        </button>
        <span className={css({ color: "#e2e8f0" })}>|</span>
        <div className={css({ flex: 1, minW: 0 })}>
          <h1 className={css({ fontSize: "md", fontWeight: "bold", color: "#0f172a", truncate: true })}>{clientName}</h1>
          <p className={css({ fontSize: "xs", color: "#94a3b8" })}>
            Historia clinica odontologica
            {record.consults.length > 0 && ` · ${record.consults.length} consulta${record.consults.length > 1 ? "s" : ""}`}
          </p>
        </div>
        <div className={css({ display: "flex", gap: "2", alignItems: "center" })}>
          <button
            type="button"
            onClick={() => void handleExportPdf()}
            disabled={isPrinting}
            className={css({
              display: "flex",
              alignItems: "center",
              gap: "1.5",
              fontSize: "xs",
              fontWeight: "medium",
              color: "#374151",
              bg: "#f1f5f9",
              px: "3",
              py: "2",
              rounded: "lg",
              border: "none",
              cursor: "pointer",
              opacity: isPrinting ? 0.6 : 1,
              _hover: { bg: "#e2e8f0" },
            })}
          >
            <FileText size={13} aria-hidden />
            <span className={css({ display: { base: "none", sm: "inline" } })}>
              {isPrinting ? "Generando…" : "Exportar PDF"}
            </span>
          </button>
          <Button size="sm" onClick={() => void handleSaveConsult()} disabled={saving}>
            <Save size={13} aria-hidden />
            {saving ? "Guardando…" : "Guardar consulta"}
          </Button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={css({
            px: "5",
            py: "2.5",
            bg: toast.type === "error" ? "#fef2f2" : "#f0fdf4",
            borderBottomWidth: "1px",
            borderColor: toast.type === "error" ? "#fecaca" : "#bbf7d0",
            fontSize: "sm",
            fontWeight: "medium",
            color: toast.type === "error" ? "#dc2626" : "#15803d",
          })}
          role="status"
          aria-live="polite"
        >
          {toast.msg}
        </div>
      )}

      {/* Odontogram */}
      <div className={css({ borderBottomWidth: "1px", borderColor: "#e2e8f0", bg: "white" })}>
        <SimpleOdontogramGrid ref={odontogramRef} />
      </div>

      {/* Form panels */}
      <div
        className={css({
          display: "grid",
          gridTemplateColumns: { base: "1fr", md: "1fr 1fr", lg: "2fr 1.5fr 1.5fr" },
          minW: 0,
        })}
      >
        {/* Nueva consulta */}
        <div className={css({ p: "5", borderRightWidth: { md: "1px" }, borderColor: "#e2e8f0", display: "flex", flexDir: "column", gap: "4", minW: 0 })}>
          <h2 className={css({ fontSize: "sm", fontWeight: "bold", color: "#0f172a" })}>Nueva consulta</h2>
          <FieldTextarea label="Motivo de consulta" value={chiefComplaint} onChange={setChiefComplaint} placeholder="Dolor en pieza 16, revision anual…" rows={2} />
          <FieldTextarea label="Notas de la sesion" value={sessionNotes} onChange={setSessionNotes} placeholder="Observaciones clinicas, hallazgos…" rows={3} />
          <FieldTextarea label="Tratamientos indicados" value={treatmentsIndicated} onChange={setTreatmentsIndicated} placeholder="Extraccion 18, obturacion 36…" rows={2} />
          <FieldTextarea label="Tratamientos realizados" value={treatmentsDone} onChange={setTreatmentsDone} placeholder="Detartraje, pulido coronal…" rows={2} />
          {resolveCustomFormFields(formsConfig?.nuevaConsulta ?? undefined).map((field) => (
            <CustomFieldRow
              key={field.id}
              field={field}
              value={customConsultAnswers[field.id]}
              onChange={(next) =>
                setCustomConsultAnswers((prev) => {
                  if (next === null) {
                    return Object.fromEntries(Object.entries(prev).filter(([k]) => k !== field.id));
                  }
                  return { ...prev, [field.id]: next };
                })
              }
            />
          ))}
          <div className={css({ display: "flex", gap: "2", justifyContent: "flex-end" })}>
            <Button size="sm" onClick={() => void handleSaveConsult()} disabled={saving}>
              <Save size={13} aria-hidden />
              {saving ? "Guardando…" : "Guardar consulta"}
            </Button>
          </div>
        </div>

        {/* Antecedentes */}
        <details className={css({ borderRightWidth: { lg: "1px" }, borderBottomWidth: { base: "1px", lg: "0" }, borderColor: "#e2e8f0", minW: 0 })}>
          <summary
            className={css({
              px: "5",
              py: "4",
              fontSize: "sm",
              fontWeight: "bold",
              color: "#0f172a",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "3",
              listStyle: "none",
              bg: "#f8fafc",
              borderBottomWidth: "1px",
              borderColor: "#e2e8f0",
              _hover: { bg: "#f1f5f9" },
              "&::-webkit-details-marker": { display: "none" },
            })}
          >
            <span className={css({ display: "flex", flexDir: "column", gap: "0.5" })}>
              Antecedentes medicos
              <span className={css({ fontSize: "xs", fontWeight: "normal", color: "#94a3b8" })}>
                Ficha médica-odontológica del paciente
              </span>
            </span>
            <ChevronDown size={18} className={css({ color: "#475569", flexShrink: 0 })} aria-hidden />
          </summary>
          <div className={css({ px: "5", pt: "5", pb: "5", display: "flex", flexDir: "column", gap: "5" })}>

            {/* Legacy — solo si hay contenido viejo y todavía no se cargó el cuestionario nuevo */}
            {!hasQuestionnaireContent(questionnaire) &&
              (record.medicalHistory || record.allergies || record.medications || record.generalNotes) && (
                <details
                  className={css({
                    borderWidth: "1px",
                    borderColor: "#fde68a",
                    bg: "#fffbeb",
                    rounded: "lg",
                    overflow: "hidden",
                  })}
                >
                  <summary
                    className={css({
                      px: "3",
                      py: "2",
                      fontSize: "xs",
                      fontWeight: "semibold",
                      color: "#92400e",
                      cursor: "pointer",
                      listStyle: "none",
                      "&::-webkit-details-marker": { display: "none" },
                    })}
                  >
                    Antecedentes (formato anterior) — completá el cuestionario nuevo abajo
                  </summary>
                  <div className={css({ px: "3", pb: "3", display: "flex", flexDir: "column", gap: "2" })}>
                    {record.medicalHistory && (
                      <p className={css({ fontSize: "xs", color: "#78350f" })}><b>Historia médica:</b> {record.medicalHistory}</p>
                    )}
                    {record.allergies && (
                      <p className={css({ fontSize: "xs", color: "#78350f" })}><b>Alergias:</b> {record.allergies}</p>
                    )}
                    {record.medications && (
                      <p className={css({ fontSize: "xs", color: "#78350f" })}><b>Medicación actual:</b> {record.medications}</p>
                    )}
                    {record.generalNotes && (
                      <p className={css({ fontSize: "xs", color: "#78350f" })}><b>Notas generales:</b> {record.generalNotes}</p>
                    )}
                  </div>
                </details>
              )}

            {/* Datos generales del paciente */}
            <div className={css({ display: "flex", flexDir: "column", gap: "3" })}>
              <h3 className={css({ fontSize: "xs", fontWeight: "bold", color: "#0f172a", textTransform: "uppercase", letterSpacing: "wide" })}>
                Datos generales
              </h3>
              <div className={css({ display: "grid", gridTemplateColumns: { base: "1fr", sm: "1fr 1fr" }, gap: "3" })}>
                <div>
                  <label className={css({ display: "block", fontSize: "xs", fontWeight: "medium", color: "#374151", mb: "1.5" })}>
                    Domicilio
                  </label>
                  <Input value={clientAddress} onChange={(e) => setClientAddress(e.target.value)} placeholder="Calle 123, ciudad" />
                </div>
                <div>
                  <label className={css({ display: "block", fontSize: "xs", fontWeight: "medium", color: "#374151", mb: "1.5" })}>
                    Fecha de nacimiento
                  </label>
                  <Input type="date" value={clientBirthDate} onChange={(e) => setClientBirthDate(e.target.value)} />
                </div>
              </div>
            </div>

            {/* Preguntas previas */}
            <div className={css({ display: "flex", flexDir: "column", gap: "3" })}>
              <div className={css({ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "3" })}>
                <span className={css({ fontSize: "sm", color: "#374151" })}>¿Está bajo cuidado médico?</span>
                <YesNoToggle value={questionnaire.underMedicalCare} onChange={(v) => setQuestionnaire((q) => ({ ...q, underMedicalCare: v }))} />
              </div>
              <div className={css({ display: "flex", flexDir: "column", gap: "1.5" })}>
                <div className={css({ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "3" })}>
                  <span className={css({ fontSize: "sm", color: "#374151" })}>¿Está recibiendo medicación en la actualidad?</span>
                  <YesNoToggle
                    value={questionnaire.currentMedication.answer}
                    onChange={(v) => setQuestionnaire((q) => ({ ...q, currentMedication: { ...q.currentMedication, answer: v } }))}
                  />
                </div>
                <Input
                  value={questionnaire.currentMedication.detail}
                  onChange={(e) => setQuestionnaire((q) => ({ ...q, currentMedication: { ...q.currentMedication, detail: e.target.value } }))}
                  placeholder="¿Cuáles?"
                />
              </div>
            </div>

            {/* Cuestionario de 25 ítems */}
            <div className={css({ display: "flex", flexDir: "column" })}>
              <h3 className={css({ fontSize: "xs", fontWeight: "bold", color: "#0f172a", textTransform: "uppercase", letterSpacing: "wide", mb: "1" })}>
                Antecedentes
              </h3>
              <div
                className={css({
                  display: "grid",
                  gridTemplateColumns: { base: "1fr", sm: "1fr 1fr" },
                  columnGap: "5",
                })}
              >
                {questionnaire.items.map((item, idx) => (
                  <QuestionnaireItemRow
                    key={item.key}
                    item={item}
                    onChange={(next) =>
                      setQuestionnaire((q) => {
                        const items = q.items.slice();
                        items[idx] = next;
                        return { ...q, items };
                      })
                    }
                  />
                ))}
              </div>
            </div>

            {/* Preguntas de cierre */}
            <div className={css({ display: "flex", flexDir: "column", gap: "3" })}>
              <div className={css({ display: "flex", flexDir: "column", gap: "1.5" })}>
                <div className={css({ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "3" })}>
                  <span className={css({ fontSize: "sm", color: "#374151" })}>¿Recuerda cuándo recibió la vacuna antitetánica?</span>
                  <YesNoToggle
                    value={questionnaire.tetanusVaccine.answer}
                    onChange={(v) => setQuestionnaire((q) => ({ ...q, tetanusVaccine: { ...q.tetanusVaccine, answer: v } }))}
                  />
                </div>
                <Input
                  value={questionnaire.tetanusVaccine.date}
                  onChange={(e) => setQuestionnaire((q) => ({ ...q, tetanusVaccine: { ...q.tetanusVaccine, date: e.target.value } }))}
                  placeholder="Fecha (aproximada está bien)"
                />
              </div>
              <div className={css({ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "3" })}>
                <span className={css({ fontSize: "sm", color: "#374151" })}>¿Está embarazada o presume estarlo?</span>
                <YesNoToggle value={questionnaire.pregnant} onChange={(v) => setQuestionnaire((q) => ({ ...q, pregnant: v }))} />
              </div>
              <div className={css({ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "3" })}>
                <span className={css({ fontSize: "sm", color: "#374151" })}>¿Está amamantando?</span>
                <YesNoToggle value={questionnaire.breastfeeding} onChange={(v) => setQuestionnaire((q) => ({ ...q, breastfeeding: v }))} />
              </div>
              <FieldTextarea
                label="Cualquier otra enfermedad no mencionada"
                value={questionnaire.otherConditions}
                onChange={(v) => setQuestionnaire((q) => ({ ...q, otherConditions: v }))}
                placeholder="Comentario general…"
                rows={2}
              />
            </div>

            <div className={css({ display: "flex", justifyContent: "flex-end" })}>
              <Button size="sm" variant="secondary" onClick={() => void handleSaveRecord()} disabled={savingRecord}>
                {savingRecord ? "Guardando…" : "Guardar antecedentes"}
              </Button>
            </div>
          </div>
        </details>

        {/* Historial / Imágenes */}
        <div className={css({ p: "5", display: "flex", flexDir: "column", gap: "3", minW: 0 })}>
          <div
            className={css({
              display: "flex",
              alignItems: "center",
              gap: "1",
              borderBottomWidth: "1px",
              borderColor: "#e2e8f0",
              mb: "1",
            })}
          >
            {(
              [
                { key: "consultas", label: `Consultas${record.consults.length > 0 ? ` (${record.consults.length})` : ""}` },
                { key: "imagenes", label: "Imágenes" },
              ] as const
            ).map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setHistoryTab(tab.key)}
                className={css({
                  px: "1",
                  pb: "2.5",
                  mb: "-1px",
                  fontSize: "sm",
                  fontWeight: "bold",
                  bg: "transparent",
                  border: "none",
                  borderBottomWidth: "2px",
                  borderColor: historyTab === tab.key ? "token(colors.brand.600)" : "transparent",
                  color: historyTab === tab.key ? "#0f172a" : "#94a3b8",
                  cursor: "pointer",
                  transition: "color 120ms ease, border-color 120ms ease",
                  _hover: { color: "#0f172a" },
                })}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {historyTab === "consultas" ? (
            record.consults.length === 0 ? (
              <div className={css({ py: "8", display: "flex", flexDir: "column", alignItems: "center", gap: "2", color: "#94a3b8" })}>
                <Calendar size={28} aria-hidden />
                <p className={css({ fontSize: "sm", textAlign: "center" })}>Aun no hay consultas registradas.</p>
                <p className={css({ fontSize: "xs", textAlign: "center" })}>Completa el formulario de la izquierda y guarda la primera consulta.</p>
              </div>
            ) : (
              record.consults.map((c) => (
                <ConsultRow
                  key={c.id}
                  c={c}
                  onDelete={(cid) => void handleDeleteConsult(cid)}
                  onLoadToOdontogram={(cid) => void handleLoadConsult(cid)}
                  onPrint={(cid) => void handleExportPdf(cid)}
                />
              ))
            )
          ) : (
            <OdontogramImagesPanel clientId={record.clientId} />
          )}
        </div>
      </div>

      {confirmLeave && (
        <UnsavedDialog
          onProceed={() => navigate("/dashboard/odontogram")}
          onCancel={() => setConfirmLeave(false)}
        />
      )}
    </div>
  );
}
